import type { Permission } from '../types/permissions'
import type { ApiPermissionsMap } from '../types/api'

const ACTION_TO_PERMISSION = {
  CREATE: 'create',
  READ: 'view',
  UPDATE: 'update',
  DELETE: 'delete',
} as const

type RawPermissions = string[] | ApiPermissionsMap | null | undefined

export const normalizePermissions = (rawPermissions: RawPermissions): string[] => {
  if (!rawPermissions) return []

  if (Array.isArray(rawPermissions)) {
    return Array.from(
      new Set(rawPermissions.filter((permission): permission is string => typeof permission === 'string')),
    )
  }

  const mapped = Object.entries(rawPermissions).flatMap(([resource, actions]) =>
    actions
      .map((action) => ACTION_TO_PERMISSION[action])
      .filter(Boolean)
      .map((action) => `${resource}:${action}`),
  )

  return Array.from(new Set(mapped))
}

/**
 * Checks if a user has a required permission.
 * Supports exact match, resource wildcard (plans:*), and global wildcard (*:*)
 */
export const hasPermission = (userPermissions: string[], required: Permission): boolean => {
  if (userPermissions.includes('*:*')) return true

  const [resource] = required.split(':')
  if (userPermissions.includes(`${resource}:*`)) return true

  return userPermissions.includes(required)
}
